import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import api from '../services/api'
import SortableTh, { sortRows } from '../components/SortableTh'
import Paginacao, { usePaginacao } from '../components/Paginacao'
import { useAuth } from '../contexts/AuthContext'
import { useConfig } from '../contexts/ConfigContext'
import { escapeHtml } from '../utils/html'
import SenhaMasterModal from '../components/SenhaMasterModal'
import IconExcluir from '../components/icons/IconExcluir'
import IconVisualizar from '../components/icons/IconVisualizar'
import { avisar } from '../components/Avisos'

const fmtDataDossie = (d) => d ? new Date(d.split('T')[0] + 'T00:00:00').toLocaleDateString('pt-BR') : '—'
const fmtValorDossie = (v) => v != null ? `R$ ${parseFloat(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '—'

// Dossie do titular (LGPD, direito de acesso): reune num unico documento tudo que o sistema tem
// sobre o cliente, pra atender um pedido de "o que voces tem sobre mim". Mesmo padrao de
// impressao ja usado em Indicacoes/Renovacoes/CRM (window.open + document.write), com todo campo
// de dado escapado (escapeHtml) pelo mesmo motivo do achado de XSS corrigido nessas telas.
function gerarDossie(dados, corPrimaria) {
  const c = dados.cliente
  const linhasTabela = (titulo, colunas, linhas) => {
    if (!linhas.length) return `<h3>${escapeHtml(titulo)}</h3><p class="vazio">Nenhum registro.</p>`
    const th = colunas.map(col => `<th>${escapeHtml(col[0])}</th>`).join('')
    const linhasHtml = linhas.map(l => `<tr>${colunas.map(col => `<td>${escapeHtml(col[1](l))}</td>`).join('')}</tr>`).join('')
    return `<h3>${escapeHtml(titulo)} (${linhas.length})</h3><table><thead><tr>${th}</tr></thead><tbody>${linhasHtml}</tbody></table>`
  }

  const html = `<!DOCTYPE html><html><head>
<meta charset="utf-8"><title>Dossiê LGPD - ${escapeHtml(c.nome)}</title>
<style>
  body{font-family:Arial,sans-serif;margin:0;padding:0;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact;color:#333;font-size:12px;}
  .page{max-width:900px;margin:0 auto;padding:24px;}
  .hdr{background:${corPrimaria};color:#fff;padding:16px 20px;border-radius:6px;margin-bottom:16px;}
  .hdr h1{margin:0;font-size:16px;}
  .hdr p{margin:4px 0 0;font-size:11px;opacity:.9;}
  h3{color:${corPrimaria};font-size:13px;text-transform:uppercase;letter-spacing:.04em;border-bottom:2px solid #eee;padding-bottom:4px;margin:18px 0 8px;}
  table{width:100%;border-collapse:collapse;margin-bottom:4px;}
  th{text-align:left;padding:6px 8px;background:#f5f5f5;font-size:10px;text-transform:uppercase;color:#777;}
  td{padding:6px 8px;border-bottom:1px solid #eee;}
  .vazio{color:#999;font-style:italic;margin:0 0 8px;}
  .dados-pessoais td{padding:4px 8px 4px 0;border:none;}
  .dados-pessoais td:first-child{color:#777;width:160px;}
  .footer{text-align:center;font-size:10px;color:#aaa;margin-top:24px;border-top:1px solid #eee;padding-top:10px;}
  @media print{@page{size:A4;margin:12mm;}}
</style></head><body>
<div class="page">
  <div class="hdr"><h1>DOSSIÊ DO TITULAR — DADOS PESSOAIS (LGPD)</h1><p>Gerado em ${new Date().toLocaleString('pt-BR')}</p></div>
  <h3>Dados Cadastrais</h3>
  <table class="dados-pessoais">
    <tr><td>Nome</td><td>${escapeHtml(c.nome)}</td></tr>
    <tr><td>Nome Social</td><td>${escapeHtml(c.nome_social) || '—'}</td></tr>
    <tr><td>CPF/CNPJ</td><td>${escapeHtml(c.cpf_cnpj)}</td></tr>
    <tr><td>Data de Nascimento</td><td>${fmtDataDossie(c.data_nascimento)}</td></tr>
    <tr><td>Telefones</td><td>${escapeHtml((c.telefones || []).map(t => t.numero).join(', ')) || '—'}</td></tr>
    <tr><td>E-mails</td><td>${escapeHtml((c.emails || []).join(', ')) || '—'}</td></tr>
    <tr><td>Endereço${c.endereco_tipo ? ' (' + escapeHtml(c.endereco_tipo) + ')' : ''}</td><td>${escapeHtml([c.endereco, c.numero, c.complemento, c.bairro, c.cidade, c.estado].filter(Boolean).join(', ')) || '—'}</td></tr>
    ${(Array.isArray(c.enderecos_adicionais) ? c.enderecos_adicionais : []).map(e => `<tr><td>Endereço${e.tipo ? ' (' + escapeHtml(e.tipo) + ')' : ''}</td><td>${escapeHtml([e.endereco, e.numero, e.complemento, e.bairro, e.cidade, e.estado].filter(Boolean).join(', ')) || '—'}</td></tr>`).join('')}
    <tr><td>Cliente desde</td><td>${fmtDataDossie(c.created_at)}</td></tr>
  </table>
  ${linhasTabela('Apólices e Propostas', [
    ['Nº', a => a.numero_apolice || a.emissao_numero_apolice || a.numero_proposta || `#${a.id}`],
    ['Seguradora', a => a.seguradora || '—'], ['Ramo', a => a.ramo || '—'],
    ['Vigência', a => `${fmtDataDossie(a.data_inicio)} a ${fmtDataDossie(a.data_vencimento)}`],
    ['Status', a => a.status || '—'], ['Prêmio Total', a => fmtValorDossie(a.premio_total)],
  ], dados.apolices)}
  ${linhasTabela('Sinistros', [
    ['Nº', s => s.numero_sinistro || `#${s.id}`], ['Data', s => fmtDataDossie(s.data_ocorrencia)],
    ['Tipo', s => s.tipo_sinistro || '—'], ['Status', s => s.status || '—'],
    ['Indenização', s => fmtValorDossie(s.valor_indenizacao)],
  ], dados.sinistros)}
  ${linhasTabela('Assistências', [
    ['Nº', a => a.numero_assistencia || `#${a.id}`], ['Data', a => fmtDataDossie(a.data_abertura)],
    ['Tipo', a => a.tipo_assistencia || '—'], ['Status', a => a.status || '—'],
  ], dados.assistencias)}
  ${linhasTabela('Lançamentos Financeiros', [
    ['Descrição', f => f.descricao || '—'], ['Tipo', f => f.tipo || '—'],
    ['Valor', f => fmtValorDossie(f.valor)], ['Vencimento', f => fmtDataDossie(f.data_vencimento)],
    ['Status', f => f.status || '—'],
  ], dados.financeiro)}
  ${linhasTabela('Negociações (CRM)', [
    ['Etapa', n => n.etapa || '—'], ['Tipo', n => n.tipo || '—'], ['Ramo', n => n.ramo || '—'],
    ['Valor Potencial', n => fmtValorDossie(n.valor_potencial)], ['Criado em', n => fmtDataDossie(n.created_at)],
  ], dados.negociacoes)}
  <div class="footer">Documento gerado pelo sistema Corretora — uso interno / atendimento a solicitação do titular (LGPD)</div>
</div>
<script>setTimeout(()=>window.print(),400)</script>
</body></html>`

  const win = window.open('', '_blank')
  if (win) { win.document.write(html); win.document.close() }
  else avisar('Permita pop-ups para gerar o documento.')
}

const colunasClientes = [
  { key: 'nome',      label: 'NOME',      get: c => c.nome || '' },
  { key: 'cpf',       label: 'CPF/CNPJ',  get: c => c.cpf_cnpj || '' },
  { key: 'telefone',  label: 'TELEFONE',  get: c => c.telefones?.[0]?.numero || '' },
  { key: 'email',     label: 'E-MAIL',    get: c => c.emails?.[0] || '' },
  { key: 'apolices',  label: 'APÓLICE',   get: c => c.total_apolices || 0, numeric: true },
  { key: 'status',    label: 'STATUS',    get: c => c.status || '' },
]

const irParaApolices = (clienteId, navigate) => {
  sessionStorage.setItem('filtroClienteId', clienteId)
  navigate('/apolices')
}

const initForm = {
  nome: '', nome_social: '', cpf_cnpj: '', sexo: '', data_nascimento: '', rg: '', estado_civil: '',
  profissao: '', cep: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '',
  telefones: [{ numero: '', nome: '' }],
  endereco_tipo: '', enderecos_adicionais: [],
  emails: [''],
  quem_indicou: '', observacoes: '', status: 'ativo'
}

const estadoCivilOpts = ['Solteiro(a)', 'Casado(a)', 'Divorciado(a)', 'Viúvo(a)', 'União Estável']
// Sugestoes da especificacao do endereco (campo livre, com lista de sugestoes).
const TIPOS_ENDERECO = ['Residencial', 'Comercial', 'Cobrança', 'Correspondência', 'Veraneio', 'Local de Risco', 'Outro']
const estados = ['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT','PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO']

const maskCPFCNPJ = v => {
  v = v.replace(/\D/g, '')
  if (v.length <= 11) {
    v = v.replace(/(\d{3})(\d)/, '$1.$2')
    v = v.replace(/(\d{3})(\d)/, '$1.$2')
    v = v.replace(/(\d{3})(\d{1,2})$/, '$1-$2')
  } else {
    v = v.replace(/^(\d{2})(\d)/, '$1.$2')
    v = v.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    v = v.replace(/\.(\d{3})(\d)/, '.$1/$2')
    v = v.replace(/(\d{4})(\d)/, '$1-$2')
  }
  return v.slice(0, 18)
}

const maskPhone = v => {
  v = v.replace(/\D/g, '')
  if (v.length <= 10) {
    v = v.replace(/(\d{2})(\d)/, '($1) $2')
    v = v.replace(/(\d{4})(\d)/, '$1-$2')
  } else {
    v = v.replace(/(\d{2})(\d)/, '($1) $2')
    v = v.replace(/(\d{5})(\d)/, '$1-$2')
  }
  return v.slice(0, 15)
}

const maskCEP = v => {
  v = v.replace(/\D/g, '')
  v = v.replace(/(\d{5})(\d)/, '$1-$2')
  return v.slice(0, 9)
}

const sec = (title) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '20px 0 12px', paddingBottom: 6, borderBottom: '2px solid var(--primary-light)' }}>
    {title}
  </div>
)

export default function Clientes() {
  const navigate = useNavigate()
  const location = useLocation()
  const { usuario, ehGestor } = useAuth()
  const { config } = useConfig()
  const [exportandoDossie, setExportandoDossie] = useState(false)
  const exportarDossie = async (id) => {
    setExportandoDossie(true)
    try {
      const { data } = await api.get(`/clientes/${id}/dossie`)
      gerarDossie(data, config.cor_primaria)
    } catch (err) {
      avisar('Erro ao gerar dossiê: ' + (err.response?.data?.erro || err.message))
    } finally {
      setExportandoDossie(false)
    }
  }
  const [clientes, setClientes] = useState([])
  const [busca, setBusca] = useState('')
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(initForm)
  const [editId, setEditId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [buscandoCep, setBuscandoCep] = useState(false)
  const [confirmarExcluir, setConfirmarExcluir] = useState(null) // { id, nome }
  const [pedirSenhaMasterExcluir, setPedirSenhaMasterExcluir] = useState(false)
  const [voltarLabel, setVoltarLabel] = useState(null)
  const voltarHandler = () => {
    const voltar = sessionStorage.getItem('voltarPara')
    if (voltar) { sessionStorage.removeItem('voltarPara'); navigate(voltar) }
    else setModal(false)
  }
  const [sortCol, setSortCol] = useState(null)
  const [sortDir, setSortDir] = useState('asc')
  const toggleSort = (key) => {
    if (sortCol === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(key); setSortDir('asc') }
  }

  // resumo=1: so as colunas da tabela (lista mais leve). O cadastro completo e buscado no abrirModal.
  const carregar = () => api.get('/clientes?resumo=1').then(r => { setClientes(r.data); return r.data })

  useEffect(() => {
    carregar().then(dados => {
      // Abrir cliente específico (vindo de outra tela)
      const clienteId = sessionStorage.getItem('abrirClienteId')
      if (clienteId) {
        sessionStorage.removeItem('abrirClienteId')
        const label = sessionStorage.getItem('voltarClienteLabel')
        if (label) { sessionStorage.removeItem('voltarClienteLabel'); setVoltarLabel(label) }
        const c = dados?.find(x => String(x.id) === String(clienteId))
        if (c) setTimeout(() => abrirModal(c), 100)
        return
      }
      // Abrir modal de novo cliente (vindo do CRM)
      const novo = sessionStorage.getItem('novoCliente') || (location.state?.novoCliente ? '1' : '')
      if (novo) {
        sessionStorage.removeItem('novoCliente')
        const prefillRaw = sessionStorage.getItem('novoClientePrefill') || (location.state?.novoClientePrefill ? JSON.stringify(location.state.novoClientePrefill) : '')
        sessionStorage.removeItem('novoClientePrefill')
        const nome = sessionStorage.getItem('novoClienteNome') || ''
        const cpfCnpj = sessionStorage.getItem('novoClienteCpfCnpj') || ''
        sessionStorage.removeItem('novoClienteNome')
        sessionStorage.removeItem('novoClienteCpfCnpj')

        let prefill = { nome, cpf_cnpj: cpfCnpj }
        if (prefillRaw) {
          try {
            prefill = {
              ...prefill,
              ...JSON.parse(prefillRaw)
            }
          } catch {}
        }

        if (prefill.nome) sessionStorage.setItem('novoClienteNome', prefill.nome)
        if (prefill.cpf_cnpj) sessionStorage.setItem('novoClienteCpfCnpj', prefill.cpf_cnpj)

        setTimeout(() => abrirModal({
          ...initForm,
          ...prefill,
          telefones: prefill.telefones?.length ? prefill.telefones : initForm.telefones,
          emails: prefill.emails?.length ? prefill.emails : initForm.emails
        }), 100)
      }
    })
  }, [location.state])

  const initials = nome => nome?.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase()
  const avatarColor = ['av-blue', 'av-green', 'av-amber']

  const abrirModal = async (c = null) => {
    // A lista vem resumida (sem endereco, RG, observacoes...): cliente existente precisa do cadastro
    // completo antes de preencher o form, senao salvar a edicao apagaria os campos que faltaram.
    if (c?.id) {
      try {
        const { data } = await api.get(`/clientes/${c.id}`)
        c = data
      } catch (err) {
        avisar('Erro ao abrir cliente: ' + (err.response?.data?.erro || err.message))
        return
      }
    }
    setEditId(c?.id || null)
    setForm(c ? {
      nome: c.nome || '', nome_social: c.nome_social || '', cpf_cnpj: c.cpf_cnpj || '', sexo: c.sexo || '',
      data_nascimento: c.data_nascimento ? c.data_nascimento.split('T')[0] : '',
      rg: c.rg || '', estado_civil: c.estado_civil || '', profissao: c.profissao || '',
      cep: c.cep || '', endereco: c.endereco || '', numero: c.numero || '',
      complemento: c.complemento || '', bairro: c.bairro || '', cidade: c.cidade || '', estado: c.estado || '',
      endereco_tipo: c.endereco_tipo || '',
      enderecos_adicionais: Array.isArray(c.enderecos_adicionais) ? c.enderecos_adicionais : [],
      telefones: c.telefones?.length ? c.telefones : [{ numero: '', nome: '' }],
      emails: c.emails?.length ? c.emails : [''],
      quem_indicou: c.quem_indicou || '', observacoes: c.observacoes || '', status: c.status || 'ativo'
    } : initForm)
    setModal(true)
  }

  const buscarCep = async (cep) => {
    const raw = cep.replace(/\D/g, '')
    if (raw.length !== 8) return
    setBuscandoCep(true)
    try {
      const res = await fetch(`https://viacep.com.br/ws/${raw}/json/`)
      const data = await res.json()
      if (!data.erro) setForm(f => ({ ...f, endereco: data.logradouro, bairro: data.bairro, cidade: data.localidade, estado: data.uf }))
    } catch {}
    finally { setBuscandoCep(false) }
  }

  // Enderecos alem do principal, cada um com a especificacao (Residencial, Comercial...).
  const addEndereco = () => setForm(f => ({ ...f, enderecos_adicionais: [...f.enderecos_adicionais, { tipo: '', cep: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '' }] }))
  const remEndereco = i => setForm(f => ({ ...f, enderecos_adicionais: f.enderecos_adicionais.filter((_, idx) => idx !== i) }))
  const setEnderecoExtra = (i, campo, v) => setForm(f => {
    const lista = [...f.enderecos_adicionais]
    lista[i] = { ...lista[i], [campo]: v }
    return { ...f, enderecos_adicionais: lista }
  })
  const buscarCepExtra = async (i, cep) => {
    const raw = cep.replace(/\D/g, '')
    if (raw.length !== 8) return
    try {
      const res = await fetch(`https://viacep.com.br/ws/${raw}/json/`)
      const data = await res.json()
      if (!data.erro) setForm(f => {
        const lista = [...f.enderecos_adicionais]
        lista[i] = { ...lista[i], endereco: data.logradouro, bairro: data.bairro, cidade: data.localidade, estado: data.uf }
        return { ...f, enderecos_adicionais: lista }
      })
    } catch {}
  }

  const addTelefone = () => setForm({ ...form, telefones: [...form.telefones, { numero: '', nome: '' }] })
  const remTelefone = i => setForm({ ...form, telefones: form.telefones.filter((_, idx) => idx !== i) })
  const setTelefone = (i, campo, v) => {
    const t = [...form.telefones]
    t[i] = { ...t[i], [campo]: campo === 'numero' ? maskPhone(v) : v }
    setForm({ ...form, telefones: t })
  }

  const addEmail = () => setForm({ ...form, emails: [...form.emails, ''] })
  const remEmail = i => setForm({ ...form, emails: form.emails.filter((_, idx) => idx !== i) })
  const setEmail = (i, v) => { const e = [...form.emails]; e[i] = v; setForm({ ...form, emails: e }) }

  // CNPJ tem 14 digitos; com isso os campos so de pessoa fisica ficam desabilitados
  const isPJ = (form.cpf_cnpj || '').replace(/\D/g, '').length === 14

  const salvar = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const dados = {
        ...form,
        telefones: form.telefones.filter(t => t.numero.trim()),
        emails: form.emails.filter(e => e.trim()),
        enderecos_adicionais: form.enderecos_adicionais.filter(e => (e.endereco || '').trim() || (e.cep || '').trim())
      }
      // Pessoa juridica nao tem RG/sexo/nascimento/estado civil — descarta o que tiver sobrado
      if (isPJ) Object.assign(dados, { rg: '', sexo: '', data_nascimento: '', estado_civil: '' })
      let clienteSalvoId = editId
      if (editId) await api.put(`/clientes/${editId}`, dados)
      else {
        const { data } = await api.post('/clientes', dados)
        clienteSalvoId = data?.id || data?.cliente?.id || null
      }
      setModal(false)
      const voltar = sessionStorage.getItem('voltarPara')
      if (voltar) {
        sessionStorage.removeItem('voltarPara')
        if (clienteSalvoId) sessionStorage.setItem('clienteCriadoId', String(clienteSalvoId))
        navigate(voltar)
        return
      }
      carregar()
    } catch (err) { avisar('Erro ao salvar cliente: ' + (err.response?.data?.erro || err.message)) }
    finally { setLoading(false) }
  }

  const excluir = (id, nome) => setConfirmarExcluir({ id, nome })

  const confirmarExclusao = async (senha) => {
    await api.delete(`/clientes/${confirmarExcluir.id}`, { data: { senha_master: senha } })
    setConfirmarExcluir(null)
    setPedirSenhaMasterExcluir(false)
    setModal(false)
    carregar()
  }

  const filtrados = clientes.filter(c => {
    const q = busca.toLowerCase()
    const qRaw = q.replace(/\D/g, '')
    if (!q) return true
    if (c.nome?.toLowerCase().includes(q)) return true
    if (c.cpf_cnpj?.includes(q)) return true
    if (qRaw && c.cpf_cnpj?.replace(/\D/g, '').includes(qRaw)) return true
    if (c.emails?.some(e => e?.toLowerCase().includes(q))) return true
    if (c.telefones?.some(t => t?.numero?.includes(q) || (qRaw && t?.numero?.replace(/\D/g, '').includes(qRaw)))) return true
    return false
  })
  const filtradosOrdenados = sortRows(filtrados, colunasClientes, sortCol, sortDir)
  const paginacao = usePaginacao(filtradosOrdenados)

  return (
    <>
      {!modal && (<>
      <div style={{ padding: '32px 24px 0' }}>
        <h2 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>Clientes</h2>
        <div style={{ fontSize: 13, color: '#888', marginTop: 4, marginBottom: 20 }}>Visualize, edite e gerencie sua base de clientes.</div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, justifyContent: 'space-between', alignItems: 'center' }}>
          <input className="form-input" placeholder="Buscar por nome, CPF/CNPJ, telefone ou e-mail..." value={busca} onChange={e => setBusca(e.target.value)} style={{ maxWidth: 420, border: '2px solid rgba(var(--primary-rgb),0.15)', borderRadius: 10 }} />
          <button className="btn btn-primary" onClick={() => abrirModal()}>+ Novo Cliente</button>
        </div>
      </div>
      <div className="page-content" style={{ paddingTop: 0 }}>
        <div className="card">
          <div className="table-wrap">
            <table className="table-borda-destaque table-compacta">
              <thead>
                <tr>
                  {colunasClientes.map(c => (
                    <SortableTh key={c.key} colKey={c.key} sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>{c.label}</SortableTh>
                  ))}
                  <th style={{ textAlign: 'center' }}>AÇÕES</th>
                </tr>
              </thead>
              <tbody>
                {paginacao.itensPagina.map((c) => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 500 }}>{c.nome}</td>
                    <td style={{ textAlign: 'center' }}>{c.cpf_cnpj || '—'}</td>
                    <td style={{ textAlign: 'center' }}>{c.telefones?.[0]?.numero || '—'}</td>
                    <td style={{ textAlign: 'center' }}>{c.emails?.[0] || '—'}</td>
                    <td style={{ textAlign: 'center' }}><span className="badge badge-blue" style={{ cursor: 'pointer' }} title="Apólices vigentes — clique para ver as apólices deste cliente" onClick={() => irParaApolices(c.id, navigate)}>{c.total_apolices || 0}</span></td>
                    <td style={{ textAlign: 'center' }}><span className={`badge ${c.status === 'ativo' ? 'badge-green' : 'badge-gray'}`}>{c.status === 'ativo' ? 'Ativo' : 'Inativo'}</span></td>
                    <td style={{ textAlign: 'center' }}>
                      <button className="btn btn-secondary btn-sm btn-icon" title="Ver" style={{ color: 'var(--primary)' }} onClick={() => abrirModal(c)}><IconVisualizar size={14} /></button>
                    </td>
                  </tr>
                ))}
                {filtrados.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: '#aaa', padding: 32 }}>Nenhum cliente encontrado</td></tr>}
              </tbody>
            </table>
          </div>
          <Paginacao {...paginacao} rotulo="cliente" />
        </div>
      </div>
      </>)}

      {modal && (
        <>
          <div style={{ padding: '24px 24px 0' }}>
            <button type="button" onClick={voltarHandler} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: 13, fontWeight: 600, padding: 0, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
              ‹ Voltar para {voltarLabel || 'Clientes'}
            </button>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{editId ? 'Editar Cliente' : 'Novo Cliente'}</h2>
                <div style={{ fontSize: 13, color: '#888', marginTop: 4, marginBottom: 20 }}>
                  {editId ? 'Atualize os dados do cliente.' : 'Preencha os dados para cadastrar um novo cliente.'}
                </div>
              </div>
              {editId && ehGestor && (
                <button type="button" className="btn btn-secondary" disabled={exportandoDossie} onClick={() => exportarDossie(editId)} title="Gera um documento com tudo que o sistema guarda sobre este cliente (LGPD)">
                  {exportandoDossie ? 'Gerando...' : '📄 Exportar dados (LGPD)'}
                </button>
              )}
            </div>
          </div>
          <div className="page-content" style={{ paddingTop: 0 }}>
            <form onSubmit={salvar}>
              <div className="form-blocos" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                {/* ===== DADOS PESSOAIS ===== */}
                <div className="card">
                  {sec('Dados Pessoais')}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }} className="form-row-auto">
                    <div className="form-group">
                      <label className="form-label">Nome completo *</label>
                      <input className="form-input" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Nome Social</label>
                      <input className="form-input" value={form.nome_social} onChange={e => setForm({ ...form, nome_social: e.target.value })} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '150px 110px 110px 130px 150px 1fr', gap: 12 }} className="form-row-auto">
                    <div className="form-group">
                      <label className="form-label">CPF / CNPJ *</label>
                      <input className="form-input" value={form.cpf_cnpj} onChange={e => setForm({ ...form, cpf_cnpj: maskCPFCNPJ(e.target.value) })} placeholder="000.000.000-00" maxLength={18} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">RG</label>
                      <input className="form-input" value={isPJ ? '' : form.rg} disabled={isPJ} onChange={e => setForm({ ...form, rg: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Sexo</label>
                      <select className="form-select" value={isPJ ? '' : form.sexo} disabled={isPJ} onChange={e => setForm({ ...form, sexo: e.target.value })}>
                        <option value="">—</option>
                        <option>Masculino</option><option>Feminino</option><option>Outro</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Nascimento</label>
                      <input className="form-input" type="date" value={isPJ ? '' : form.data_nascimento} disabled={isPJ} onChange={e => setForm({ ...form, data_nascimento: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Estado Civil</label>
                      <select className="form-select" value={isPJ ? '' : form.estado_civil} disabled={isPJ} onChange={e => setForm({ ...form, estado_civil: e.target.value })}>
                        <option value="">—</option>
                        {estadoCivilOpts.map(o => <option key={o}>{o}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">{isPJ ? 'Atividade' : 'Profissão'}</label>
                      <input className="form-input" value={form.profissao} placeholder={isPJ ? 'Ramo de atividade da empresa' : ''} onChange={e => setForm({ ...form, profissao: e.target.value })} />
                    </div>
                  </div>
                </div>

                {/* ===== CONTATO ===== */}
                <div className="card">
                  {sec('Contato')}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }} className="form-row-auto">
                    <div className="form-group">
                      <label className="form-label">Telefones</label>
                      {form.telefones.map((t, i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                          <input className="form-input" value={t.numero} onChange={e => setTelefone(i, 'numero', e.target.value)} placeholder="(00) 00000-0000" maxLength={15} style={{ width: 160, flex: 'none' }} />
                          <input className="form-input" value={t.nome} onChange={e => setTelefone(i, 'nome', e.target.value)} placeholder="Nome (ex: Esposa, Trabalho...)" style={{ flex: 1 }} />
                          {/* Marca quais numeros tem WhatsApp — usados no botao WhatsApp da aba Anexos da apolice */}
                          <label title="Este número tem WhatsApp" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, whiteSpace: 'nowrap', cursor: 'pointer', color: t.whatsapp ? '#1a9e4b' : '#888', fontWeight: t.whatsapp ? 600 : 400 }}>
                            <input type="checkbox" checked={!!t.whatsapp} onChange={e => setTelefone(i, 'whatsapp', e.target.checked)} style={{ cursor: 'pointer' }} />
                            WhatsApp
                          </label>
                          {form.telefones.length > 1 && <button type="button" className="btn btn-danger btn-sm" onClick={() => remTelefone(i)}>✕</button>}
                        </div>
                      ))}
                      <button type="button" className="btn btn-secondary btn-sm" onClick={addTelefone}>+ Adicionar telefone</button>
                    </div>
                    <div className="form-group">
                      <label className="form-label">E-mails</label>
                      {form.emails.map((e, i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                          <input className="form-input" type="email" value={e} onChange={ev => setEmail(i, ev.target.value)} placeholder={i === 0 ? 'E-mail principal' : 'E-mail adicional'} />
                          {form.emails.length > 1 && <button type="button" className="btn btn-danger btn-sm" onClick={() => remEmail(i)}>✕</button>}
                        </div>
                      ))}
                      <button type="button" className="btn btn-secondary btn-sm" onClick={addEmail}>+ Adicionar e-mail</button>
                    </div>
                  </div>
                </div>

                {/* ===== ENDEREÇO ===== */}
                <div className="card">
                  {sec('Endereço')}
                  <datalist id="tipos-endereco">
                    {TIPOS_ENDERECO.map(t => <option key={t} value={t} />)}
                  </datalist>
                  <div className="form-group" style={{ maxWidth: 320 }}>
                    <label className="form-label">Especificação (endereço principal)</label>
                    <input className="form-input" list="tipos-endereco" value={form.endereco_tipo} onChange={e => setForm({ ...form, endereco_tipo: e.target.value })} placeholder="Ex: Residencial, Comercial, Cobrança..." />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr 80px 0.5fr', gap: 12 }} className="form-row-auto">
                    <div className="form-group">
                      <label className="form-label">CEP {buscandoCep && <span style={{ color: '#888', fontWeight: 400 }}>buscando...</span>}</label>
                      <input className="form-input" value={form.cep} onChange={e => { const v = maskCEP(e.target.value); setForm({ ...form, cep: v }); if (v.length === 9) buscarCep(v) }} placeholder="00000-000" maxLength={9} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Rua</label>
                      <input className="form-input" value={form.endereco} onChange={e => setForm({ ...form, endereco: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Número</label>
                      <input className="form-input" value={form.numero} onChange={e => setForm({ ...form, numero: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Complemento</label>
                      <input className="form-input" value={form.complemento} onChange={e => setForm({ ...form, complemento: e.target.value })} placeholder="Apto, Bloco..." />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 60px', gap: 12 }} className="form-row-auto">
                    <div className="form-group">
                      <label className="form-label">Bairro</label>
                      <input className="form-input" value={form.bairro} onChange={e => setForm({ ...form, bairro: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Cidade</label>
                      <input className="form-input" value={form.cidade} onChange={e => setForm({ ...form, cidade: e.target.value })} />
                    </div>
                    <div className="form-group" style={{ minWidth: 0 }}>
                      <label className="form-label">Estado</label>
                      <select className="form-select" value={form.estado} onChange={e => setForm({ ...form, estado: e.target.value })}>
                        <option value="">—</option>
                        {estados.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>

                  {form.enderecos_adicionais.map((en, i) => (
                    <div key={i} style={{ borderTop: '1px dashed #ddd', paddingTop: 12, marginTop: 4 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 4 }}>
                        <div className="form-group" style={{ flex: '0 1 320px', marginBottom: 0 }}>
                          <label className="form-label">Especificação</label>
                          <input className="form-input" list="tipos-endereco" value={en.tipo} onChange={e => setEnderecoExtra(i, 'tipo', e.target.value)} placeholder="Ex: Comercial, Veraneio..." />
                        </div>
                        <button type="button" className="btn btn-danger btn-sm" style={{ marginLeft: 'auto' }} onClick={() => remEndereco(i)}>✕ Remover endereço</button>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr 80px 0.5fr', gap: 12, marginTop: 8 }} className="form-row-auto">
                        <div className="form-group">
                          <label className="form-label">CEP</label>
                          <input className="form-input" value={en.cep} onChange={e => { const v = maskCEP(e.target.value); setEnderecoExtra(i, 'cep', v); if (v.length === 9) buscarCepExtra(i, v) }} placeholder="00000-000" maxLength={9} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Rua</label>
                          <input className="form-input" value={en.endereco} onChange={e => setEnderecoExtra(i, 'endereco', e.target.value)} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Número</label>
                          <input className="form-input" value={en.numero} onChange={e => setEnderecoExtra(i, 'numero', e.target.value)} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Complemento</label>
                          <input className="form-input" value={en.complemento} onChange={e => setEnderecoExtra(i, 'complemento', e.target.value)} placeholder="Apto, Bloco..." />
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 60px', gap: 12 }} className="form-row-auto">
                        <div className="form-group">
                          <label className="form-label">Bairro</label>
                          <input className="form-input" value={en.bairro} onChange={e => setEnderecoExtra(i, 'bairro', e.target.value)} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Cidade</label>
                          <input className="form-input" value={en.cidade} onChange={e => setEnderecoExtra(i, 'cidade', e.target.value)} />
                        </div>
                        <div className="form-group" style={{ minWidth: 0 }}>
                          <label className="form-label">Estado</label>
                          <select className="form-select" value={en.estado} onChange={e => setEnderecoExtra(i, 'estado', e.target.value)}>
                            <option value="">—</option>
                            {estados.map(s => <option key={s}>{s}</option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button type="button" className="btn btn-secondary btn-sm" onClick={addEndereco}>+ Adicionar endereço</button>
                </div>

                {/* ===== OUTROS ===== */}
                <div className="card">
                  {sec('Outros')}
                  <div className="form-group">
                    <label className="form-label">Quem indicou</label>
                    <input className="form-input" value={form.quem_indicou} onChange={e => setForm({ ...form, quem_indicou: e.target.value })} placeholder="Nome de quem indicou este cliente" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Observações</label>
                    <textarea className="form-input" rows={4} value={form.observacoes} onChange={e => setForm({ ...form, observacoes: e.target.value })} placeholder="Anotações sobre o cliente..." style={{ resize: 'vertical' }} />
                  </div>
                  {editId && (
                    <div className="form-group" style={{ maxWidth: 200 }}>
                      <label className="form-label">Status</label>
                      <select className="form-select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                        <option value="ativo">Ativo</option>
                        <option value="inativo">Inativo</option>
                      </select>
                    </div>
                  )}
                </div>

              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                {editId && (
                  <button type="button" className="btn btn-danger btn-icon" title="Excluir" onClick={() => excluir(editId, form.nome)}><IconExcluir size={16} /></button>
                )}
                <button type="button" className="btn btn-secondary" onClick={voltarHandler}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Salvando...' : 'Salvar Cliente'}</button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* Modal de confirmação de exclusão */}
      {confirmarExcluir && (
        <div className="modal-overlay" style={{ zIndex: 300 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '32px 36px', maxWidth: 400, width: '100%', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ color: '#c00', marginBottom: 12, display: 'flex', justifyContent: 'center' }}><IconExcluir size={40} /></div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#1A1A2E', marginBottom: 8 }}>Excluir cliente?</div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 24 }}>
              Tem certeza que deseja excluir <strong>{confirmarExcluir.nome}</strong>?<br />
              <span style={{ color: '#A32D2D', fontSize: 12 }}>Só é possível excluir se este cliente não tiver nenhuma Proposta/Apólice cadastrada (mesmo cancelada ou vencida).</span>
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button className="btn btn-secondary" style={{ minWidth: 100 }} onClick={() => setConfirmarExcluir(null)}>Cancelar</button>
              <button className="btn btn-danger" style={{ minWidth: 100 }} onClick={() => setPedirSenhaMasterExcluir(true)}>Sim, excluir</button>
            </div>
          </div>
        </div>
      )}

      {pedirSenhaMasterExcluir && (
        <SenhaMasterModal
          titulo="Excluir cliente?"
          mensagem="Confirme com a Senha Master para excluir este cliente definitivamente."
          onCancelar={() => setPedirSenhaMasterExcluir(false)}
          onConfirmar={confirmarExclusao}
        />
      )}

      <style>{`
        @media (max-width: 700px) {
          .form-row-auto { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  )
}
